import * as React from 'react';
import { cn } from '@restworld/utils-common';
import { Button } from '../button';

/**
 * ModalConfirmContent — standardized content layout for confirmation/success modals.
 * Replaces CommonModalWrapper from @restworld/ui-ds.
 *
 * Renders: icon + title + subtitle + primary/secondary buttons, all centered.
 * Use inside <ModalContent> as a body slot.
 */
export interface ModalConfirmContentProps {
  /** Icon rendered centered above the text block */
  icon?: React.ReactNode;
  /** Primary heading text */
  title?: string;
  /** Secondary text below title */
  subtitle?: string;
  /** Third text line below subtitle */
  additionalContent?: string;
  /** Arbitrary content inserted alongside the text/icon section */
  children?: React.ReactNode;
  /** Reverse order of content and text block */
  reversed?: boolean;
  /** Primary action button */
  primaryAction?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  /** Secondary action button */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Text alignment. Default: 'center' */
  align?: 'center' | 'left';
  className?: string;
}

export function ModalConfirmContent({
  icon,
  title,
  subtitle,
  additionalContent,
  children,
  reversed,
  primaryAction,
  secondaryAction,
  align = 'center',
  className,
}: ModalConfirmContentProps) {
  return (
    <div className={cn('flex flex-col gap-8 w-full', className)}>
      <div className={cn('flex gap-5 items-center', reversed ? 'flex-col-reverse' : 'flex-col')}>
        {icon && <div className="flex justify-center">{icon}</div>}
        <div
          className={cn('flex gap-2 flex-col max-w-sm', align === 'center' ? 'text-center items-center' : 'text-left')}
        >
          {title && <h3 className="text-2xl font-semibold text-gray-900 leading-9">{title}</h3>}
          {subtitle && <p className="text-base text-gray-500">{subtitle}</p>}
          {additionalContent && <p className="text-base text-gray-500">{additionalContent}</p>}
        </div>
        {children}
      </div>
      {(primaryAction || secondaryAction) && (
        <div className="flex flex-col gap-2 w-full">
          {primaryAction && (
            <Button fullwidth variant="primary" onClick={primaryAction.onClick} loading={primaryAction.loading}>
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button fullwidth variant="secondary-neutral" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
